import { useState } from 'react'

import { CountryCode, ParseError, parsePhoneNumberWithError } from 'libphonenumber-js'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { ArcadeLinkButton, Toast } from '@cash-design-system/react'
import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { RegisterSmsResponse, RegisterSmsResponseStatus } from '@cash-web/protos/squareup/franklin/app/sms.pb'
import { PhoneVerificationBlocker } from '@cash-web/protos/squareup/franklin/blockers/phone_number_blockers.pb'
import { SmsDeliveryMechanism } from '@cash-web/protos/squareup/franklin/common/sms_delivery_mechanism.pb'
import { PhoneVerificationBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import logger from '@cash-web/shared/util-logger'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { hasOONIntent, optionalScopeHeaders } from '@cash-web/shared-blocker-data-access-api'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationRegisterSms } from '@cash-web/shared-data-access-api/react-query/useMutationRegisterSms'
import { useMutationVerifySms } from '@cash-web/shared-data-access-api/react-query/useMutationVerifySms'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { useRegion } from '@cash-web/shared-data-access-region-provider'
import { ConfirmationCodeInput } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { getRegisterSMSErrors, getStatusError, getVerifySMSErrors } from '../../../util/statusErrors'
import { BaseBlocker, BaseBlockerFormFields, BlockerProps } from '../../BaseBlocker/BaseBlocker'
import { HelpModalItem } from '../../HelpModal/HelpModal'

interface FormFields extends BaseBlockerFormFields {
  code: string
}
interface Props extends BlockerProps {
  blocker: PhoneVerificationBlocker
  supplement?: PhoneVerificationBlockerSupplement
}

type ResendCodeType = 'text' | 'voice'

export const redactPhoneNumberForLogging = (val: string | undefined): string => {
  if (val === undefined) {
    return 'undefined'
  }

  return val.replace(/\d/g, 'D').replace(/[^D()+-.[\]\s]/g, '?')
}

export const parsePhoneNumberOrLogError = (val: string, defaultCountry?: CountryCode) => {
  let formattedNumber = ''

  try {
    const parsedNumber = parsePhoneNumberWithError(val, defaultCountry)
    // libphonenumber is able to auto detect the country when parsing and format it to the correct national format

    formattedNumber = parsedNumber.formatNational()
  } catch (error) {
    if (error instanceof ParseError) {
      logger.error(new Error(`Phone number parsing error: ${redactPhoneNumberForLogging(val)} – ${error.message}`))
    } else {
      logger.error(new Error(`Phone number parsing error: ${redactPhoneNumberForLogging(val)}`))
    }
  }
  return formattedNumber
}

export function BlockerPhoneVerification({ blocker, supplement, skippable }: Props): React.ReactElement {
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const form = useForm<FormFields>({ mode: 'onTouched' })
  const userCountry = useAuth().profile?.country_code
  const userRegionGuess = useRegion()
  const countryCode = (userCountry ?? userRegionGuess ?? 'US') as CountryCode

  const [resendCodeType, setResendCodeType] = useState<ResendCodeType>('text')
  const [isResendingCode, setIsResendingCode] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  const canShowAdditionalHelpItems = useFeatureFlag('cash-web-account-verify-alias-additional-help-items')

  const canResendCode = useFeatureFlag('cash-frontend-product-resend-code')
  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()
  const flow = useFlowContext()

  const optionalHeaders = optionalScopeHeaders()
  const isOON = hasOONIntent()

  const { mutateAsync: registerSms } = useMutationRegisterSms({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
      optionalRequestHeaders: optionalHeaders,
    },
  })

  const { mutateAsync: verifySms } = useMutationVerifySms({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
      optionalRequestHeaders: optionalHeaders,
    },
  })

  let formattedNumber = ''
  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  if (blocker.number!.toLowerCase().includes('x')) {
    formattedNumber = blocker.number!
  } else {
    formattedNumber = parsePhoneNumberOrLogError(blocker.number!, countryCode) || blocker.number || ''
  }

  const getBlockerTitle = (type: ResendCodeType) => {
    switch (type) {
      case 'text':
        return t('We sent you a code', { context: 'title' })

      case 'voice':
        return t("We're calling you with a code", { context: 'title' })
    }
  }

  const getBlockerSubtext = (type: ResendCodeType) => {
    switch (type) {
      case 'text':
        return t(
          "Check your phone for a code we sent to {phoneNumber}. If you haven't received it, we can send it again or try another way.",
          { context: 'subtitle', phoneNumber: formattedNumber }
        )
      case 'voice':
        return t(
          "Accept the call to {phoneNumber} on your phone and listen for the code. If you haven't received a call, we can call you again or send you a text.",
          { context: 'subtitle', phoneNumber: formattedNumber }
        )
    }
  }

  const onSubmit = async ({ code }: FormFields) => {
    await handleBlockerResponse({
      promisedBlockerUpdate: verifySms({
        sms_number: blocker.number,
        sms_verification_code: code,
        payment_tokens: flow.active?.paymentTokens,
      }),
      errors: getVerifySMSErrors(t, formattedNumber),
    })
  }

  const resendCode = async (verificationMechanism: SmsDeliveryMechanism) => {
    setResendCodeType(verificationMechanism === SmsDeliveryMechanism.VOICE ? 'voice' : 'text')
    let response: RegisterSmsResponse = {}
    try {
      response = await registerSms({
        sms_number: blocker.number,
        existing_customer_only: true,
        verification_mechanism: verificationMechanism,
        payment_tokens: flow.active?.paymentTokens,
      })
    } catch (e) {
      logger.error(e as Error)
    }

    if (response.status === RegisterSmsResponseStatus.SUCCESS) {
      if (verificationMechanism === SmsDeliveryMechanism.VOICE) {
        return {
          success: true,
          message: t('You will receive a call to {alias} with a code', {
            alias: formattedNumber,
            context:
              "Success text. indicating that we've successfully sent a voice call to the user's phone number. A code is a series of digits and hyphens. An alias is either a phone or an email.",
          }),
        }
      } else {
        return {
          success: true,
          message: t('A code has been sent to {alias}', {
            alias: formattedNumber,
            context:
              "Success text. indicating that we've successfully sent a code to their alias. a code is a series of digits and hyphens. An alias is either a phone or an email.",
          }),
        }
      }
    } else {
      return {
        success: false,
        message: getStatusError(getRegisterSMSErrors(genericError, t), response.status, genericError),
      }
    }
  }

  const editNumber = () => {
    flow.updateWithBlockerDescriptors({
      blockerDescriptors: [
        {
          blocker: {
            alias_blocker: {},
          },
        },
      ],
      queriesToInvalidate: [],
    })
    return Promise.resolve({ success: true })
  }

  let helpItems: HelpModalItem[] = []
  const isEntryScreen = blocker.number && !blocker.number?.includes('X')

  if (isEntryScreen || canResendCode) {
    helpItems = [
      {
        text: t('Resend code', { context: 'button text' }),
        customAction: () => resendCode(SmsDeliveryMechanism.SMS_TEXT),
      },
      {
        text: t('Receive a phone call', { context: 'button text' }),
        customAction: () => resendCode(SmsDeliveryMechanism.VOICE),
      },
    ]
  }

  const handleResendCodeClick = async () => {
    setIsResendingCode(true)
    try {
      const result = await resendCode(SmsDeliveryMechanism.SMS_TEXT)
      if (result) {
        setToastMessage(result.message)
      }
    } catch (error) {
      logger.error(error as Error)
    } finally {
      setIsResendingCode(false)
    }
  }

  const resendCodeLink = (
    <Trans
      i18nKey={'<buttonLink>Resend code</buttonLink>'}
      context="Prompt for customers to resend code."
      components={{
        buttonLink: (
          <ArcadeLinkButton ariaLabel="Resend code" onClick={handleResendCodeClick} isLoading={isResendingCode} />
        ),
      }}
    />
  )

  // we only want to show edit phone if the phone is not redacted (only if they've entered it)
  if (isEntryScreen && flow.active?.clientScenario === ClientScenario.ONBOARDING) {
    helpItems = helpItems.concat({ text: t('Edit number', { context: 'menu option' }), customAction: editNumber })
  }

  if (supplement?.additional_help_items && canShowAdditionalHelpItems) {
    helpItems = helpItems.concat(supplement.additional_help_items)
  }

  const getBlockerConfig = () => {
    const baseConfig = {
      mainText: t('Enter the code sent to your phone', { context: 'title' }),
      form,
      onSubmit,
      skippable,
    }

    if (isOON) {
      return baseConfig
    }

    return {
      ...baseConfig,
      secondaryText: t('We sent the code to {phoneNumber}.', {
        context: 'subtitle',
        phoneNumber: formattedNumber,
      }),
      helpModal: {
        helpItems,
        title: getBlockerTitle(resendCodeType),
        subtext: getBlockerSubtext(resendCodeType),
      },
    }
  }

  return isArcadeMigrationEnabled ? (
    <>
      <BaseBlocker
        {...getBlockerConfig()}
        footer={
          <>
            {t('Didn’t receive a code?', { context: 'subtitle text' })} {resendCodeLink}
          </>
        }
      >
        <ConfirmationCodeInput showLabel={false} />
      </BaseBlocker>
      {toastMessage && (
        <Toast
          headline={toastMessage}
          position="top-center"
          onClose={() => setToastMessage('')}
          data-testid="resend-code-message"
        />
      )}
    </>
  ) : (
    <BaseBlocker {...getBlockerConfig()}>
      <ConfirmationCodeInput />
    </BaseBlocker>
  )
}
